import { useEffect, useRef } from 'react';
import {
	NavigationType,
	useLocation,
	useNavigationType,
} from 'react-router-dom';
import { appScrollToTop } from '../lib/appScroll';

/**
 * Reset the scroll position on route navigation (PD-14672).
 *
 * React Router preserves the scroll offset across navigations — so
 * tapping into a detail page from a scrolled list (invoice line → item,
 * order → item, catalog deep in the grid) opened the new page already
 * scrolled down, with the back link pinned to the top edge. Every route
 * change lands at the top instead.
 *
 * The reset goes through `appScrollToTop` because the scroll offset
 * lives on the window on desktop but on `document.body` in the iOS
 * WKWebView (see `lib/appScroll.ts`) — the original window-only call was
 * a silent no-op in the native app.
 *
 * Keyed on `location.key` (not just pathname) so navigations between two
 * instances of the SAME route — e.g. product → related product — also reset.
 * Back/forward (POP) keeps the browser's own restoration so returning to a
 * long list doesn't lose the user's place.
 *
 * A query-string-only change is deliberately NOT a reset (PD-15471). The
 * catalog writes its category, facet and page selections into the URL, so
 * every filter click was landing here as a fresh navigation and throwing the
 * shopper back to the top of the page — most visibly on the dashboard, where
 * the catalog sits below the account widgets and picking a category scrolled
 * right past it. Pagination is unaffected: every paginated page (catalog,
 * orders, invoices, statements, transactions) calls `appScrollToTop` itself
 * from its own page-change handler.
 */
export function ScrollToTop(): null {
	const location = useLocation();
	const navigationType = useNavigationType();
	const lastPathnameRef = useRef<string | null>(null);

	useEffect(() => {
		const previousPathname = lastPathnameRef.current;
		lastPathnameRef.current = location.pathname;

		if (navigationType === NavigationType.Pop) {
			return;
		}
		if (previousPathname === location.pathname) {
			return;
		}
		// 'auto' (not smooth) — an instant jump reads as a fresh page, while
		// animated scrolling on navigation looks like a glitch.
		appScrollToTop('auto');
	}, [location.key, location.pathname, navigationType]);

	return null;
}
